import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { toast } from 'sonner';
import {
  Save,
  RotateCcw,
  Layout,
  Type,
  QrCode,
  User,
  Tag,
  Ticket,
  Building,
  Briefcase,
  Shirt,
  Eye,
  EyeOff,
  AlignLeft,
  AlignCenter,
  AlignRight,
  FileImage,
  Download,
  Loader2,
  Plus,
  X,
  Mail,
  Phone,
  Hash,
  UtensilsCrossed
} from 'lucide-react';

// Badge dimensions in mm (A6 portrait: 105mm × 148mm)
const BADGE_WIDTH_MM = 105;
const BADGE_HEIGHT_MM = 148;

// Scale factor for preview (mm to pixels) - balanced size
const SCALE = 3.5;

// Available fonts
const FONTS = [
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Helvetica-Bold', label: 'Helvetica Bold' },
  { value: 'Times-Roman', label: 'Times Roman' },
  { value: 'Times-Bold', label: 'Times Bold' },
  { value: 'Courier', label: 'Courier' },
  { value: 'Courier-Bold', label: 'Courier Bold' },
];

// Core fields that cannot be removed from a badge template
const CORE_FIELD_IDS = new Set(['category_name', 'name', 'qr_code']);

// Registry of all known standard attendee fields with metadata and default styling
const STANDARD_FIELDS_REGISTRY = {
  category_name: {
    label: 'Category', placeholder: 'VIP DELEGATE',
    defaults: { x: 0, y: 0, width: 105, height: 20, font_size: 16, font_color: '#FFFFFF', font_family: 'Helvetica-Bold', font_weight: 'bold', text_align: 'center', bg_color: '#3B82F6', resizable: false }
  },
  qr_code: {
    label: 'QR Code', placeholder: null,
    defaults: { x: 25, y: 30, width: 55, height: 55, resizable: true }
  },
  name: {
    label: 'Attendee Name', placeholder: 'John Doe',
    defaults: { x: 5, y: 95, width: 95, height: 18, font_size: 20, font_color: '#000000', font_family: 'Helvetica-Bold', font_weight: 'bold', text_align: 'center' }
  },
  call_name: {
    label: 'Call Name', placeholder: 'Johnny',
    defaults: { x: 5, y: 108, width: 95, height: 12, font_size: 14, font_color: '#333333', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  designation: {
    label: 'Designation', placeholder: 'Director',
    defaults: { x: 5, y: 115, width: 95, height: 12, font_size: 12, font_color: '#555555', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  club_name: {
    label: 'Club/Organization', placeholder: 'Rotary Club of Mumbai',
    defaults: { x: 5, y: 115, width: 95, height: 14, font_size: 14, font_color: '#333333', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  ticket_id: {
    label: 'Ticket ID', placeholder: 'ID: TKT-001234',
    defaults: { x: 5, y: 135, width: 95, height: 10, font_size: 12, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  tshirt_size: {
    label: 'T-Shirt Size', placeholder: 'XL',
    defaults: { x: 75, y: 135, width: 25, height: 10, font_size: 10, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center', bg_color: '#f0f0f0' }
  },
  email: {
    label: 'Email', placeholder: 'john@example.com',
    defaults: { x: 5, y: 128, width: 95, height: 10, font_size: 9, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  phone_number: {
    label: 'Phone Number', placeholder: '+91 98765 43210',
    defaults: { x: 5, y: 128, width: 95, height: 10, font_size: 9, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  gender: {
    label: 'Gender', placeholder: 'Male',
    defaults: { x: 5, y: 128, width: 95, height: 10, font_size: 9, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  district_number: {
    label: 'District Number', placeholder: '3141',
    defaults: { x: 5, y: 128, width: 95, height: 10, font_size: 9, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  },
  dietary_preference: {
    label: 'Dietary Preference', placeholder: 'Vegetarian',
    defaults: { x: 5, y: 128, width: 95, height: 10, font_size: 9, font_color: '#666666', font_family: 'Helvetica', font_weight: 'normal', text_align: 'center' }
  }
};

// Build a field object from the registry (or generate defaults for custom fields)
function buildFieldObject(fieldId, overrides = {}) {
  const registry = STANDARD_FIELDS_REGISTRY[fieldId];
  if (registry) {
    return {
      field_id: fieldId,
      label: registry.label,
      visible: true,
      resizable: false,
      placeholder: registry.placeholder,
      bg_color: '',
      ...registry.defaults,
      ...overrides
    };
  }
  // Custom field — generate sensible defaults
  return {
    field_id: fieldId,
    label: fieldId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    x: 5, y: 120, width: 95, height: 12,
    font_size: 11, font_color: '#555555', font_family: 'Helvetica', font_weight: 'normal',
    text_align: 'center', bg_color: '',
    visible: true, resizable: false,
    placeholder: fieldId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    ...overrides
  };
}

// Initial fields for new templates (matches previous hardcoded set)
const INITIAL_FIELD_IDS = [
  { id: 'category_name' },
  { id: 'qr_code' },
  { id: 'name' },
  { id: 'designation', overrides: { visible: false } },
  { id: 'club_name' },
  { id: 'ticket_id' },
  { id: 'tshirt_size', overrides: { visible: false } },
];

// Default field configurations for A6 badge (105mm × 148mm)
const DEFAULT_FIELDS = INITIAL_FIELD_IDS.map(({ id, overrides }) => buildFieldObject(id, overrides));

// Preset layouts for A6 badge (105mm × 148mm)
const PRESET_LAYOUTS = {
  standard: {
    name: 'Standard',
    description: 'Category at top, QR centered, name below',
    fields: DEFAULT_FIELDS
  },
  qr_top: {
    name: 'QR at Top',
    description: 'Large QR at top, details below',
    fields: DEFAULT_FIELDS.map(f => {
      if (f.field_id === 'category_name') return { ...f, y: 90, height: 18 };
      if (f.field_id === 'qr_code') return { ...f, y: 10, x: 25, width: 55, height: 55 };
      if (f.field_id === 'name') return { ...f, y: 75, font_size: 18 };
      if (f.field_id === 'designation') return { ...f, y: 95 };
      if (f.field_id === 'club_name') return { ...f, y: 110 };
      if (f.field_id === 'ticket_id') return { ...f, y: 130 };
      if (f.field_id === 'tshirt_size') return { ...f, y: 130 };
      return f;
    })
  },
  qr_bottom: {
    name: 'QR at Bottom',
    description: 'Name prominent at top, QR at bottom',
    fields: DEFAULT_FIELDS.map(f => {
      if (f.field_id === 'category_name') return { ...f, y: 0, height: 20 };
      if (f.field_id === 'name') return { ...f, y: 28, font_size: 22 };
      if (f.field_id === 'designation') return { ...f, y: 48 };
      if (f.field_id === 'club_name') return { ...f, y: 50, visible: true };
      if (f.field_id === 'qr_code') return { ...f, y: 75, x: 25, width: 50, height: 50 };
      if (f.field_id === 'ticket_id') return { ...f, y: 135 };
      if (f.field_id === 'tshirt_size') return { ...f, y: 135 };
      return f;
    })
  },
  minimal: {
    name: 'Minimal',
    description: 'Clean design with essentials only',
    fields: DEFAULT_FIELDS.map(f => {
      if (f.field_id === 'category_name') return { ...f, y: 5, height: 18, font_size: 18 };
      if (f.field_id === 'qr_code') return { ...f, y: 35, x: 30, width: 45, height: 45 };
      if (f.field_id === 'name') return { ...f, y: 95, font_size: 22 };
      if (f.field_id === 'ticket_id') return { ...f, y: 125, font_size: 14 };
      if (f.field_id === 'designation') return { ...f, visible: false };
      if (f.field_id === 'club_name') return { ...f, visible: false };
      if (f.field_id === 'tshirt_size') return { ...f, visible: false };
      return f;
    })
  },
  detailed: {
    name: 'Detailed',
    description: 'All fields visible with full info',
    fields: DEFAULT_FIELDS.map(f => ({
      ...f,
      visible: true,
      ...(f.field_id === 'category_name' ? { y: 0, height: 18, font_size: 14 } : {}),
      ...(f.field_id === 'qr_code' ? { y: 25, x: 25, width: 55, height: 55 } : {}),
      ...(f.field_id === 'name' ? { y: 85, font_size: 18 } : {}),
      ...(f.field_id === 'designation' ? { y: 100, font_size: 11, visible: true } : {}),
      ...(f.field_id === 'club_name' ? { y: 112, font_size: 12 } : {}),
      ...(f.field_id === 'ticket_id' ? { y: 127, font_size: 11, x: 5, width: 60 } : {}),
      ...(f.field_id === 'tshirt_size' ? { y: 127, font_size: 11, x: 70, width: 30, visible: true } : {})
    }))
  }
};

const BadgeTemplateEditor = ({ 
  eventId, 
  categoryId, 
  templateData, 
  onSave, 
  onClose 
}) => {
  const { token } = useAuth();
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [selectedField, setSelectedField] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const previewRef = useRef(null);

  // Dynamic field states
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [customFieldNames, setCustomFieldNames] = useState([]);

  // Load template data
  useEffect(() => {
    if (templateData) {
      // Load background image - prefer preview_image for editor display
      const imgSource = templateData.preview_image || templateData.template_file;
      if (imgSource) {
        const imgSrc = imgSource.startsWith('data:') 
          ? imgSource 
          : `data:image/png;base64,${imgSource}`;
        setBackgroundImage(imgSrc);
      }
      
      // Load field mappings or use defaults
      if (templateData.field_mappings && templateData.field_mappings.length > 0) {
        const mergedFields = [];
        // 1. Start with DEFAULT_FIELDS, merge saved values for matching field_ids
        for (const defaultField of DEFAULT_FIELDS) {
          const savedField = templateData.field_mappings.find(
            f => f.field_id === defaultField.field_id
          );
          mergedFields.push(savedField ? { ...defaultField, ...savedField } : { ...defaultField });
        }
        // 2. Restore dynamic fields (saved but not in DEFAULT_FIELDS)
        for (const savedField of templateData.field_mappings) {
          if (!DEFAULT_FIELDS.some(d => d.field_id === savedField.field_id)) {
            const base = buildFieldObject(savedField.field_id);
            mergedFields.push({ ...base, ...savedField });
          }
        }
        setFields(mergedFields);
      } else {
        // No saved mappings, use defaults
        setFields([...DEFAULT_FIELDS]);
      }
    }
  }, [templateData]);

  // Fetch custom field names for this event
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await api.get(`/events/${eventId}/custom-fields`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomFieldNames(response.data || []);
      } catch (error) {
        // Custom fields may not exist yet, that's ok
        setCustomFieldNames([]);
      }
    };
    if (eventId) fetchCustomFields();
  }, [eventId, token]);

  // Fetch category info
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await api.get(`/events/${eventId}/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const cat = response.data.find(c => c.category_id === categoryId);
        if (cat) {
          setCategoryInfo(cat);
          // Update category placeholder with actual name
          setFields(prev => prev.map(f => 
            f.field_id === 'category_name' 
              ? { ...f, placeholder: cat.category_name.toUpperCase() }
              : f
          ));
        }
      } catch (error) {
        console.error('Error fetching category:', error);
      }
    };
    if (eventId && categoryId) {
      fetchCategory();
    }
  }, [eventId, categoryId, token]);

  const handleAddField = (fieldId) => {
    const newField = buildFieldObject(fieldId);
    // Position below the lowest existing visible field
    const visibleFields = fields.filter(f => f.visible && f.field_id !== 'qr_code');
    const maxY = visibleFields.length > 0
      ? Math.max(...visibleFields.map(f => f.y + f.height))
      : 100;
    newField.y = Math.min(maxY + 2, BADGE_HEIGHT_MM - newField.height - 2);
    setFields(prev => [...prev, newField]);
    setSelectedField(fieldId);
    setAddFieldOpen(false);
    toast.success(`Added "${newField.label}" to badge`);
  };

  const handleRemoveField = (fieldId) => {
    if (CORE_FIELD_IDS.has(fieldId)) return;
    setFields(prev => prev.filter(f => f.field_id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const handleFieldChange = useCallback((fieldId, updates) => {
    setFields(prev => prev.map(f => 
      f.field_id === fieldId ? { ...f, ...updates } : f
    ));
  }, []);

  const handleDragStop = useCallback((fieldId, d) => {
    const x = Math.max(0, Math.min(d.x / SCALE, BADGE_WIDTH_MM - 10));
    const y = Math.max(0, Math.min(d.y / SCALE, BADGE_HEIGHT_MM - 5));
    handleFieldChange(fieldId, { x, y });
  }, [handleFieldChange]);

  const handleResizeStop = useCallback((fieldId, ref, position) => {
    const width = parseInt(ref.style.width) / SCALE;
    const height = parseInt(ref.style.height) / SCALE;
    const x = position.x / SCALE;
    const y = position.y / SCALE;
    handleFieldChange(fieldId, { width, height, x, y });
  }, [handleFieldChange]);

  const applyPreset = (presetKey) => {
    const preset = PRESET_LAYOUTS[presetKey];
    if (preset) {
      const presetFieldIds = new Set(preset.fields.map(f => f.field_id));
      // Keep category name from current data
      const updatedFields = preset.fields.map(f => {
        if (f.field_id === 'category_name' && categoryInfo) {
          return { ...f, placeholder: categoryInfo.category_name.toUpperCase() };
        }
        return f;
      });
      // Preserve dynamic fields (not in preset), but hide them
      for (const field of fields) {
        if (!presetFieldIds.has(field.field_id)) {
          updatedFields.push({ ...field, visible: false });
        }
      }
      setFields(updatedFields);
      toast.success(`Applied "${preset.name}" layout`);
    }
  };

  const resetToDefaults = () => {
    const resetFields = DEFAULT_FIELDS.map(f => {
      if (f.field_id === 'category_name' && categoryInfo) {
        return { ...f, placeholder: categoryInfo.category_name.toUpperCase() };
      }
      return f;
    });
    setFields(resetFields);
    setSelectedField(null);
    toast.info('Reset to default layout');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save field mappings to template
      await api.put(`/events/${eventId}/badge-templates/${templateData.template_id}/field-mappings`, {
        field_mappings: fields
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Badge template saved! All badges for this category will use these positions.');
      if (onSave) onSave(fields);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await api.post(
        `/events/${eventId}/badge-templates/${templateData.template_id}/preview`,
        { field_mappings: fields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPreviewData(response.data);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadPreview = () => {
    if (!previewData?.badge_pdf) return;
    
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${previewData.badge_pdf}`;
    link.download = `badge_preview_${previewData.sample_attendee?.ticket_id || 'sample'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Preview badge downloaded');
  };

  const getFieldIcon = (fieldId) => {
    const iconMap = {
      qr_code: QrCode, name: User, call_name: User, category_name: Tag,
      ticket_id: Ticket, designation: Briefcase, club_name: Building,
      tshirt_size: Shirt, email: Mail, phone_number: Phone,
      gender: User, district_number: Hash, dietary_preference: UtensilsCrossed
    };
    const Icon = iconMap[fieldId] || Type;
    return <Icon className="h-4 w-4" />;
  };

  const renderFieldPreview = (field) => {
    if (!field.visible) return null;

    // Scale font size based on SCALE factor for preview
    const scaledFontSize = (field.font_size || 10) * SCALE * 0.28; // 0.28 is approximate mm to px ratio
    
    const style = {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: field.text_align === 'left' ? 'flex-start' : 
                      field.text_align === 'right' ? 'flex-end' : 'center',
      textAlign: field.text_align || 'center',
      fontSize: `${scaledFontSize}px`,
      fontFamily: field.font_family?.includes('Bold') ? 'Arial Black, Arial' : 'Arial',
      fontWeight: field.font_weight === 'bold' ? 'bold' : 'normal',
      color: field.font_color || '#000000',
      backgroundColor: field.bg_color || 'transparent',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: '4px 8px',
      boxSizing: 'border-box',
      cursor: 'move',
      userSelect: 'none',
      lineHeight: 1.2,
    };

    const isSelected = selectedField === field.field_id;
    const borderStyle = isSelected 
      ? '2px solid #3B82F6' 
      : '1px dashed rgba(100,100,100,0.4)';

    if (field.field_id === 'qr_code') {
      return (
        <Rnd
          key={field.field_id}
          size={{ width: field.width * SCALE, height: field.height * SCALE }}
          position={{ x: field.x * SCALE, y: field.y * SCALE }}
          onDragStop={(e, d) => handleDragStop(field.field_id, d)}
          onResizeStop={(e, dir, ref, delta, pos) => handleResizeStop(field.field_id, ref, pos)}
          bounds="parent"
          enableResizing={true}
          lockAspectRatio={true}
          onClick={() => setSelectedField(field.field_id)}
          style={{ border: borderStyle, background: '#fff', borderRadius: '4px' }}
        >
          <div style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: '#f5f5f5',
            borderRadius: '4px'
          }}>
            <QrCode className="text-gray-500" style={{ width: '70%', height: '70%' }} />
          </div>
        </Rnd>
      );
    }

    return (
      <Rnd
        key={field.field_id}
        size={{ width: field.width * SCALE, height: field.height * SCALE }}
        position={{ x: field.x * SCALE, y: field.y * SCALE }}
        onDragStop={(e, d) => handleDragStop(field.field_id, d)}
        onResizeStop={(e, dir, ref, delta, pos) => handleResizeStop(field.field_id, ref, pos)}
        bounds="parent"
        enableResizing={true}
        minWidth={30}
        minHeight={20}
        onClick={() => setSelectedField(field.field_id)}
        style={{ border: borderStyle }}
      >
        <div style={style}>
          {field.placeholder || field.label}
        </div>
      </Rnd>
    );
  };

  const selectedFieldData = fields.find(f => f.field_id === selectedField);

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="badge-template-editor">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b bg-white flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold">Badge Template Editor</h2>
          <p className="text-sm text-gray-500">
            Drag fields to reposition • Click to edit properties
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={previewLoading}>
            {previewLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileImage className="h-4 w-4 mr-2" />
            )}
            Preview with Sample
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Panel - Preview */}
        <div className="flex-1 p-4 bg-gray-100 overflow-auto">
          <div className="flex items-start justify-center min-h-full">
            <div 
              ref={previewRef}
              className="relative bg-white shadow-lg flex-shrink-0 border border-gray-300"
              style={{
                width: BADGE_WIDTH_MM * SCALE,
                height: BADGE_HEIGHT_MM * SCALE,
              }}
              data-testid="badge-preview"
              onClick={(e) => {
                // Deselect field when clicking on empty space
                if (e.target === e.currentTarget) {
                  setSelectedField(null);
                }
              }}
            >
              {/* Background template image with reduced opacity for editing */}
              {backgroundImage && (
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.35,
                  }}
                />
              )}
              
              {/* Render all visible fields */}
              {fields.map(field => renderFieldPreview(field))}
              
              {/* Center alignment guides */}
              <div 
                className="absolute pointer-events-none"
                style={{
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  background: 'linear-gradient(to bottom, rgba(239, 68, 68, 0.7) 50%, transparent 50%)',
                  backgroundSize: '2px 8px',
                  transform: 'translateX(-1px)',
                }}
              />
              <div 
                className="absolute pointer-events-none"
                style={{
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(to right, rgba(239, 68, 68, 0.7) 50%, transparent 50%)',
                  backgroundSize: '8px 2px',
                  transform: 'translateY(-1px)',
                }}
              />
              
              {/* Grid overlay - 10mm grid aligned with badge */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, rgba(200,200,200,0.3) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(200,200,200,0.3) 1px, transparent 1px)
                  `,
                  backgroundSize: `${10 * SCALE}px ${10 * SCALE}px`,
                  backgroundPosition: '0 0'
                }}
              />
              {/* Badge border outline */}
              <div className="absolute inset-0 border-2 border-dashed border-blue-300 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Right Panel - Controls */}
        <div className="w-80 border-l bg-white flex flex-col flex-shrink-0">
          <Tabs defaultValue="fields" className="flex flex-col h-full">
            <TabsList className="w-full flex-shrink-0">
              <TabsTrigger value="fields" className="flex-1">Fields</TabsTrigger>
              <TabsTrigger value="presets" className="flex-1">Presets</TabsTrigger>
            </TabsList>

            <TabsContent value="fields" className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Field List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Badge Fields</Label>
                {fields.map(field => (
                  <div
                    key={field.field_id}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                      selectedField === field.field_id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    onClick={() => setSelectedField(field.field_id)}
                    data-testid={`field-item-${field.field_id}`}
                  >
                    <div className="flex items-center gap-2">
                      {getFieldIcon(field.field_id)}
                      <span className={field.visible ? '' : 'text-gray-400'}>{field.label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={field.visible}
                        onCheckedChange={(checked) => handleFieldChange(field.field_id, { visible: checked })}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {!CORE_FIELD_IDS.has(field.field_id) && (
                        <button
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          onClick={(e) => { e.stopPropagation(); handleRemoveField(field.field_id); }}
                          title="Remove field"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add Field Button */}
                <Popover open={addFieldOpen} onOpenChange={setAddFieldOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full mt-2">
                      <Plus className="h-4 w-4 mr-2" /> Add Field
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search fields..." />
                      <CommandList>
                        <CommandEmpty>No fields found.</CommandEmpty>
                        {(() => {
                          const existingIds = new Set(fields.map(f => f.field_id));
                          const availableStandard = Object.entries(STANDARD_FIELDS_REGISTRY)
                            .filter(([id]) => !existingIds.has(id))
                            .map(([id, meta]) => ({ id, label: meta.label }));
                          const availableCustom = customFieldNames
                            .filter(name => !existingIds.has(name) && !STANDARD_FIELDS_REGISTRY[name]);
                          return (
                            <>
                              {availableStandard.length > 0 && (
                                <CommandGroup heading="Standard Fields">
                                  {availableStandard.map(({ id, label }) => (
                                    <CommandItem key={id} onSelect={() => handleAddField(id)} className="cursor-pointer">
                                      <span className="mr-2">{getFieldIcon(id)}</span>
                                      {label}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )}
                              {availableCustom.length > 0 && (
                                <CommandGroup heading="Custom Fields">
                                  {availableCustom.map(name => (
                                    <CommandItem key={name} onSelect={() => handleAddField(name)} className="cursor-pointer">
                                      <Type className="h-4 w-4 mr-2" />
                                      {name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )}
                              {availableStandard.length === 0 && availableCustom.length === 0 && (
                                <div className="py-6 text-center text-sm text-gray-500">
                                  All available fields have been added
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Selected Field Properties */}
              {selectedFieldData && selectedFieldData.field_id !== 'qr_code' && (
                <Card className="mt-4">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      {getFieldIcon(selectedFieldData.field_id)}
                      {selectedFieldData.label} Properties
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Font Size */}
                    <div>
                      <Label className="text-xs">Font Size (pt)</Label>
                      <Input
                        type="number"
                        min={5}
                        max={24}
                        value={selectedFieldData.font_size || 10}
                        onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                          font_size: parseInt(e.target.value) || 10 
                        })}
                      />
                    </div>

                    {/* Font Family */}
                    <div>
                      <Label className="text-xs">Font Family</Label>
                      <Select
                        value={selectedFieldData.font_family || 'Helvetica'}
                        onValueChange={(value) => handleFieldChange(selectedFieldData.field_id, { 
                          font_family: value,
                          font_weight: value.includes('Bold') ? 'bold' : 'normal'
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONTS.map(font => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Font Color */}
                    <div>
                      <Label className="text-xs">Font Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={selectedFieldData.font_color || '#000000'}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            font_color: e.target.value 
                          })}
                          className="w-12 h-9 p-1"
                        />
                        <Input
                          type="text"
                          value={selectedFieldData.font_color || '#000000'}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            font_color: e.target.value 
                          })}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    {/* Background Color */}
                    <div>
                      <Label className="text-xs">Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={selectedFieldData.bg_color || '#ffffff'}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            bg_color: e.target.value 
                          })}
                          className="w-12 h-9 p-1"
                        />
                        <Input
                          type="text"
                          value={selectedFieldData.bg_color || ''}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            bg_color: e.target.value 
                          })}
                          placeholder="transparent"
                          className="flex-1"
                        />
                      </div>
                    </div>

                    {/* Text Alignment */}
                    <div>
                      <Label className="text-xs">Text Alignment</Label>
                      <div className="flex gap-1 mt-1">
                        <Button
                          variant={selectedFieldData.text_align === 'left' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleFieldChange(selectedFieldData.field_id, { text_align: 'left' })}
                        >
                          <AlignLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={selectedFieldData.text_align === 'center' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleFieldChange(selectedFieldData.field_id, { text_align: 'center' })}
                        >
                          <AlignCenter className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={selectedFieldData.text_align === 'right' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleFieldChange(selectedFieldData.field_id, { text_align: 'right' })}
                        >
                          <AlignRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Position (read-only, controlled by drag) */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">X (mm)</Label>
                        <Input
                          type="number"
                          value={Math.round(selectedFieldData.x)}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            x: parseInt(e.target.value) || 0 
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Y (mm)</Label>
                        <Input
                          type="number"
                          value={Math.round(selectedFieldData.y)}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            y: parseInt(e.target.value) || 0 
                          })}
                        />
                      </div>
                    </div>

                    {/* Size */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Width (mm)</Label>
                        <Input
                          type="number"
                          min={10}
                          max={105}
                          value={Math.round(selectedFieldData.width)}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            width: parseInt(e.target.value) || 50 
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Height (mm)</Label>
                        <Input
                          type="number"
                          min={5}
                          max={50}
                          value={Math.round(selectedFieldData.height)}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            height: parseInt(e.target.value) || 15 
                          })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* QR Code Properties */}
              {selectedFieldData && selectedFieldData.field_id === 'qr_code' && (
                <Card className="mt-4">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <QrCode className="h-4 w-4" />
                      QR Code Properties
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Size (mm)</Label>
                        <Input
                          type="number"
                          min={15}
                          max={40}
                          value={Math.round(selectedFieldData.width)}
                          onChange={(e) => {
                            const size = parseInt(e.target.value) || 25;
                            handleFieldChange(selectedFieldData.field_id, { 
                              width: size,
                              height: size
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">X (mm)</Label>
                        <Input
                          type="number"
                          value={Math.round(selectedFieldData.x)}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            x: parseInt(e.target.value) || 0 
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Y (mm)</Label>
                        <Input
                          type="number"
                          value={Math.round(selectedFieldData.y)}
                          onChange={(e) => handleFieldChange(selectedFieldData.field_id, { 
                            y: parseInt(e.target.value) || 0 
                          })}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Tip: Larger QR codes scan better. Minimum recommended size is 20mm.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="presets" className="p-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Preset Layouts</Label>
                <p className="text-xs text-gray-500 mb-4">
                  Click a preset to apply it. You can customize after applying.
                </p>
                
                {Object.entries(PRESET_LAYOUTS).map(([key, preset]) => (
                  <Card 
                    key={key}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => applyPreset(key)}
                    data-testid={`preset-${key}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <Layout className="h-4 w-4 text-blue-500" />
                        <div>
                          <p className="font-medium text-sm">{preset.name}</p>
                          <p className="text-xs text-gray-500">{preset.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileImage className="h-5 w-5" />
              Badge Preview
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              {/* Sample Attendee Info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-700 mb-1">Sample Attendee Data:</p>
                <div className="grid grid-cols-2 gap-1 text-gray-600">
                  <span>Name:</span>
                  <span className="font-medium">{previewData.sample_attendee?.name}</span>
                  <span>Ticket ID:</span>
                  <span className="font-medium">{previewData.sample_attendee?.ticket_id}</span>
                  {previewData.sample_attendee?.club_name && (
                    <>
                      <span>Organization:</span>
                      <span className="font-medium">{previewData.sample_attendee?.club_name}</span>
                    </>
                  )}
                  <span>Category:</span>
                  <span className="font-medium">{previewData.category?.name}</span>
                </div>
              </div>

              {/* Badge Preview Image */}
              <div className="flex justify-center">
                <div className="border rounded-lg overflow-hidden shadow-lg bg-white">
                  <iframe
                    src={`data:application/pdf;base64,${previewData.badge_pdf}`}
                    width="216"
                    height="344"
                    title="Badge Preview"
                    className="border-0"
                  />
                </div>
              </div>

              {/* Download Button */}
              <div className="flex justify-center gap-2">
                <Button onClick={downloadPreview} className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Download Preview PDF
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                This preview uses {previewData.sample_attendee?.name === 'John Doe' ? 'placeholder' : 'actual'} attendee data.
                Save the template to apply these settings to all badges.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BadgeTemplateEditor;
